import React from 'react';

import { type Theme, styled } from '@mui/material/styles';

import AppLessTopBar from './AppLessTopBar';

type ContentLayout = 'default' | 'page';

const getAppLessContentMinHeight = (theme: Theme) => {
  const toolbarHeights = theme.mixins.toolbar as Record<string, { minHeight?: number }>;
  const defaultToolbarHeight = toolbarHeights.minHeight ?? 56;
  const landscapeToolbarHeight =
    toolbarHeights['@media (min-width:0px) and (orientation: landscape)']?.minHeight ??
    defaultToolbarHeight;
  const desktopToolbarHeight =
    toolbarHeights[theme.breakpoints.up('sm')]?.minHeight ?? defaultToolbarHeight;

  return {
    minHeight: `calc(100vh - ${defaultToolbarHeight}px)`,
    '@media (min-width:0px) and (orientation: landscape)': {
      minHeight: `calc(100vh - ${landscapeToolbarHeight}px)`,
    },
    [theme.breakpoints.up('sm')]: {
      minHeight: `calc(100vh - ${desktopToolbarHeight}px)`,
    },
  };
};

const Page = styled('div', {
  shouldForwardProp: (prop) => prop !== 'contentLayout',
})<{ contentLayout?: ContentLayout }>(({ contentLayout = 'default', theme }) => ({
  minHeight: '100vh',
  backgroundColor: contentLayout === 'page' ? theme.palette.common.white : theme.palette.grey[100],
}));

const Content = styled('main', {
  shouldForwardProp: (prop) => prop !== 'centerContent' && prop !== 'contentLayout',
})<{
  centerContent?: boolean;
  contentLayout?: ContentLayout;
}>(({ centerContent = false, contentLayout = 'default', theme }) => {
  if (contentLayout === 'page') {
    return {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      ...getAppLessContentMinHeight(theme),
      '& > *': {
        width: '100%',
      },
    };
  }

  return {
    display: 'flex',
    alignItems: centerContent ? 'center' : 'flex-start',
    justifyContent: 'center',
    ...getAppLessContentMinHeight(theme),
    padding: centerContent ? theme.spacing(3) : `${theme.spacing(3)} 0 ${theme.spacing(2)}`,
  };
});

type Props = React.PropsWithChildren<{
  centerContent?: boolean;
  contentLayout?: ContentLayout;
  pageTestId?: string;
}>;

const AppLessShell = ({
  centerContent = false,
  children,
  contentLayout = 'default',
  pageTestId = 'no-organisation-access-page',
}: Props) => {
  return (
    <Page contentLayout={contentLayout} data-fd={pageTestId}>
      <AppLessTopBar />
      <Content centerContent={centerContent} contentLayout={contentLayout}>
        {children}
      </Content>
    </Page>
  );
};

export default AppLessShell;
